"use client";

import Link from "next/link";
import Image from "next/image";
import { usePathname } from "next/navigation";
import { useSession, signOut } from "next-auth/react";
import { useRef, useState, useEffect, useMemo } from "react";
import CartDropdown from "./CartDropdown";
import { useCartStore } from "@/stores/cartStore";
import { useWebSocketStore } from "@/stores/websocketStore";
import CartModal from "./CartModal";

interface MainMenuProps {
  variant?: "dark" | "light" | "market";
}

const CartSection = () => {
  const { data: session, status } = useSession();
  const {
    guestCart,
    loggedInCart,
    removeFromCart,
    fetchCart,
    addToCart,
  } = useCartStore();
  const cartButtonRef = useRef<HTMLButtonElement>(null);
  const cartDropdownRef = useRef<HTMLDivElement>(null);
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Select the appropriate cart based on status
  const cartItems = status === "authenticated" ? loggedInCart.cartItems : guestCart.cartItems;

  useEffect(() => {
    if (status === "authenticated" && session?.user?.id) {
      console.log("Login detected. Fetching cart for user:", session.user.id);
      const userId = session.user.id.toString();
      fetchCart(userId);

      const apiUrl = process.env.NEXT_PUBLIC_API_URL;
      if (!apiUrl) {
        console.error("NEXT_PUBLIC_API_URL is not defined in environment variables");
        return;
      }
      const wsUrl = apiUrl.replace(/^https?:\/\//, "wss://") + "/media-updates";
      const ws = useWebSocketStore.getState().connections.get(wsUrl);
      if (ws && session.user.id) {
      const handleMessage = async (event: MessageEvent) => {
        const data = JSON.parse(event.data);
        if (data.type === "cart_updated") {
          await fetchCart(userId);
        } else if (data.type === "media_updated") {
          const updatedMedia = data.payload;
          useCartStore.setState((state) => {
            const exists = state.loggedInCart.cartItems.some(
              (item) => item.media_id === updatedMedia.id
            );
            if (exists) {
              const updatedCartItems = state.loggedInCart.cartItems.map((item) =>
                item.media_id === updatedMedia.id
                  ? { ...item, media: { ...item.media, ...updatedMedia } }
                  : item
              );
              const subtotal = updatedCartItems.reduce(
                (sum, item) => sum + item.media.market_price * item.quantity,
                0
              );
              const discount = state.loggedInCart.promo
                ? state.loggedInCart.promo.discount_applied
                : 0;
              return {
                loggedInCart: {
                  ...state.loggedInCart,
                  cartItems: updatedCartItems,
                  subtotal,
                  total: subtotal - discount,
                },
              };
            }
            return state;
          });
        } else if (data.type === "media_removed") {
          const removedMediaId = data.payload.id;
          useCartStore.setState((state) => {
            const updatedCartItems = state.loggedInCart.cartItems.filter(
              (item) => item.media_id !== removedMediaId
            );
            const subtotal = updatedCartItems.reduce(
              (sum, item) => sum + item.media.market_price * item.quantity,
              0
            );
            const discount = state.loggedInCart.promo
              ? state.loggedInCart.promo.discount_applied
              : 0;
            return {
              loggedInCart: {
                ...state.loggedInCart,
                cartItems: updatedCartItems,
                subtotal,
                total: subtotal - discount,
              },
            };
          });
        }
      };
      ws.addEventListener("message", handleMessage);
      return () => {
        ws.removeEventListener("message", handleMessage);
      };
    }
  } else if (status === "unauthenticated") {
    console.log("User logged out. Loading guest cart from persisted state.");
  }
}, [status, session, fetchCart]);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      const target = event.target as Node | null;

      if (
        cartButtonRef.current &&
        cartDropdownRef.current &&
        target &&
        !cartButtonRef.current.contains(target) &&
        !cartDropdownRef.current.contains(target)
      ) {
        setIsCartOpen(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleViewCartClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    setIsModalOpen(true);
    setIsCartOpen(false);
  };

  return (
    <>
      <div className="cart-group-wrapper">
        <button
          ref={cartButtonRef}
          className="btn dropdown-toggle"
          type="button"
          aria-expanded={isCartOpen}
          onClick={() => setIsCartOpen((prev) => !prev)}
        >
          <Image
            src="/images/icon/200-w.svg"
            alt="Cart"
            className="m-auto w-cart"
            width={24}
            height={24}
          />
          {cartItems.length > 0 && (
            <span className="item-count">{cartItems.length}</span>
          )}
        </button>
        {isCartOpen && cartButtonRef.current && (
          <div ref={cartDropdownRef}>
            <CartDropdown>
              {cartItems.length === 0 ? (
                <div className="empty-cart-message font-rubik">
                  Your cart is empty{" "}
                  <Image
                    src="/images/icon/empty-cart.svg"
                    width={25}
                    height={25}
                    alt="Empty cart"
                  />
                </div>
              ) : (
                <>
                  <ul className="style-none cart-product-list">
                    {Array.isArray(cartItems) &&
                      cartItems.map((item) => (
                        <li
                          key={item.id}
                          className="d-flex align-items-center selected-item"
                        >
                          <Link
                            href={`/marketplace?search=${encodeURIComponent(
                              item.media.display_name
                            )}&id=${item.media_id}`}
                            className="item-img d-flex align-items-center justify-content-center"
                          >
                            <Image
                              src={`/images/media-logos/${
                                item.media.img_final || "media_default_img-2.webp"
                              }`}
                              alt={item.media.display_name}
                              width={50}
                              height={50}
                              onError={(e) => {
                                (e.target as HTMLImageElement).src =
                                  "/images/media-logos/media_default_img-2.webp";
                              }}
                            />
                          </Link>
                          <div className="item-info">
                            <Link
                              href={`/marketplace?search=${encodeURIComponent(
                                item.media.display_name
                              )}&id=${item.media_id}`}
                              className="name"
                            >
                              {item.media.display_name}
                            </Link>
                            <div className="price">
                              $
                              {item.media.market_price.toLocaleString("en-US", {
                                minimumFractionDigits: 0,
                                maximumFractionDigits: 2,
                              })}{" "}
                              {session?.user?.id && item.project?.name && (
                                <span className="proj-name">{item.project.name}</span>
                              )}
                            </div>
                            <Link
                              href=""
                              className="cart-delete"
                              onClick={async (e) => {
                                e.preventDefault();
                                await removeFromCart(item.id);
                              }}
                            >
                              ✕
                            </Link>
                          </div>
                        </li>
                      ))}
                  </ul>
                  <div className="subtotal d-flex justify-content-between align-items-center">
                    <div className="title">
                      Subtotal <span className="ml-0">(USD)</span>
                    </div>
                    <div className="total-price">
                      $
                      {cartItems
                        .reduce(
                          (sum, item) => sum + item.media.market_price * item.quantity,
                          0
                        )
                        .toLocaleString("en-US", {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}
                    </div>
                  </div>
                  <ul className="style-none button-group">
                    <li>
                      <Link
                        href="#"
                        className="view-cart"
                        onClick={handleViewCartClick}
                      >
                        View Cart {cartItems.length > 0 && `(${cartItems.length})`}
                      </Link>
                    </li>
                    <li>
                      <Link href="/checkout" className="checkout">
                        Checkout
                      </Link>
                    </li>
                  </ul>
                </>
              )}
            </CartDropdown>
          </div>
        )}
      </div>
      <CartModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />
    </>
  );
};

export default function MainMenu({ variant = "dark" }: MainMenuProps) {
  const { data: session, status } = useSession();
  const fetchCart = useCartStore((state) => state.fetchCart);
  const pathname = usePathname();

  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isSupportOpen, setIsSupportOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [isDesktopDropdownOpen, setIsDesktopDropdownOpen] = useState(false);

  const isAdmin = session?.user?.userType === 1;

  const navItems = isAdmin
    ? [
        { name: "Admin", path: "/admin", key: "admin" },
        { name: "Dashboard", path: "/dashboard", key: "dashboard" },
        { name: "Marketplace", path: "/marketplace", key: "marketplace" },
        { name: "Support", path: "/support", key: "support", dropdown: true },
      ]
    : session
    ? [
        { name: "Dashboard", path: "/dashboard", key: "dashboard" },
        { name: "Marketplace", path: "/marketplace", key: "marketplace" },
        { name: "Orders", path: "/dashboard/orders", key: "orders" },
        { name: "Support", path: "/support", key: "support", dropdown: true },
      ]
    : [
        { name: "Home", path: "/", key: "home" },
        { name: "Marketplace", path: "/marketplace", key: "marketplace" },
        { name: "Company", path: "/company", key: "company" },
        { name: "Support", path: "/support", key: "support", dropdown: true },
      ];

  const activeMenuItem = useMemo(() => {
    if (pathname.startsWith("/support")) return "support";
    if (pathname === "/marketplace") return "marketplace";
    if (session) {
      if (isAdmin && pathname === "/admin") return "admin";
      if (pathname === "/dashboard/orders") return "orders";
      if (pathname.startsWith("/dashboard")) return "dashboard";
    }
    if (!session && pathname === "/company") return "company";
    if (pathname === "/") return "home";
    return "home";
  }, [pathname, session, isAdmin]);

  const isTermsPoliciesActive = pathname.startsWith("/support/legal");

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      const target = event.target as Node | null;
      const mobileMenuEl = document.querySelector(".custom-mobile-menu");
      const userMenuEl = document.querySelector(".custom-user-menu");
      const desktopUserDropdownEl = document.querySelector(".desktop-user-dropdown");

      if (
        mobileMenuEl &&
        target &&
        !mobileMenuEl.contains(target) &&
        !(target instanceof Element && target.closest(".custom-mobile-toggle"))
      ) {
        setIsMobileMenuOpen(false);
      }

      if (
        userMenuEl &&
        target &&
        !userMenuEl.contains(target) &&
        !(target instanceof Element && target.closest(".mobile-user-toggle"))
      ) {
        setIsUserMenuOpen(false);
      }

      if (
        desktopUserDropdownEl &&
        target &&
        !desktopUserDropdownEl.contains(target) &&
        !(target instanceof Element && target.closest(".user-dropdown"))
      ) {
        setIsDesktopDropdownOpen(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  if (status === "loading") {
    return null;
  }

  const lightVariants = ["light", "market-l"];
  const logoSrc = lightVariants.includes(variant)
    ? "/images/logo/av_logo_blk.svg"
    : "/images/logo/av_logo.svg";
  const loginIconSrc = lightVariants.includes(variant)
    ? "/images/icon/52.svg"
    : "/images/icon/120.svg";

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen((prev) => !prev);
  };

  // Compute userName safely
  const userName = session?.user?.name ?? session?.user?.email?.split('@')[0] ?? "User";

  const avatarUrl = session?.user?.avatar
    ? `/images/users/avatar/${session.user.avatar}.webp`
    : "/images/users/avatar/fallback.webp";

  return (
    <div className={`theme-main-menu sticky-menu theme-menu-seven menu-var-${variant} bg-none`} style={{ zIndex: 99 }}>
      <div className="d-flex align-items-center justify-content-between">
        <div className="av logo order-lg-1">
          <Link href="/">
            <Image src={logoSrc} alt="Avelon Logo" width={175} height={33} />
          </Link>
        </div>
        <div className="right-widget order-lg-3 d-flex align-items-center">
          <CartSection />
          {session && session.user && (
            <button
              className="mobile-user-toggle d-lg-none"
              onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
              aria-label="Toggle user menu"
            >
              <Image
                src={avatarUrl}
                alt="User Avatar"
                className="user-avatar"
                width={30}
                height={30}
                onError={(e) => {
                  (e.target as HTMLImageElement).src = "/images/users/avatar/fallback.webp";
                }}
              />
            </button>
          )}
          <ul className="d-flex align-items-center">
            {session && session.user ? (
              <li
                className="user-dropdown d-none d-lg-flex align-items-center"
                onMouseEnter={() => setIsDesktopDropdownOpen(true)}
                onMouseLeave={() => setIsDesktopDropdownOpen(false)}
              >
                <Image
                  src={avatarUrl}
                  alt="User Avatar"
                  className="user-avatar"
                  width={37}
                  height={37}
                  onError={(e) => {
                    (e.target as HTMLImageElement).src = "/images/users/avatar/fallback.webp";
                  }}
                />
                <span className="desktop-user-hey">Hey, {userName}</span>
                {isDesktopDropdownOpen && (
                  <div className="desktop-user-dropdown">
                    <ul className="desktop-user-menu font-gordita">
                      <li>
                        <Link
                          href="/dashboard/orders"
                          className={`dropdown-item ${pathname === "/dashboard/orders" ? "active" : ""}`}
                        >
                          My Orders
                        </Link>
                      </li>
                      <li>
                        <Link
                          href="/dashboard/projects"
                          className={`dropdown-item ${pathname === "/dashboard/projects" ? "active" : ""}`}
                        >
                          My Projects
                        </Link>
                      </li>
                      <li>
                        <Link
                          href="/dashboard/account"
                          className={`dropdown-item ${pathname === "/dashboard/account" ? "active" : ""}`}
                        >
                          My Profile
                        </Link>
                      </li>
                      <li className="separator"></li>
                      <li>
                        <button
                          className="dropdown-item signout-btn"
                          onClick={async () => {
                            try {
                              const token = session?.user?.token;
                              if (!token) {
                                console.warn("No authentication token available for token revocation");
                                return;
                              }
                              const apiUrl = process.env.NEXT_PUBLIC_API_URL;
                              if (!apiUrl) {
                                console.error("NEXT_PUBLIC_API_URL is not defined in environment variables");
                                return;
                              }
                              const response = await fetch(`${apiUrl}/auth/revoke`, {
                                method: "POST",
                                headers: {
                                  "Content-Type": "application/json",
                                  "Authorization": `Bearer ${token}`,
                                },
                                body: JSON.stringify({
                                  user_id: session?.user?.id,
                                  userAgent: navigator.userAgent || "Unknown",
                                  ipAddress: "Unknown",
                                }),
                              });
                              if (!response.ok) {
                                console.error("Failed to revoke token:", await response.text());
                              }
                            } catch (error) {
                              console.error("Error revoking token:", error);
                            }
                            await signOut({ redirect: true, callbackUrl: "/auth" });
                          }}
                        >
                          Sign Out
                        </button>
                      </li>
                    </ul>
                  </div>
                )}
              </li>
            ) : (
              <>
                <li>
                  <Link href="/auth" className="signIn-action d-flex align-items-center">
                    <Image src={loginIconSrc} alt="Login Icon" width={24} height={24} />
                    <span>login</span>
                  </Link>
                </li>
                <li>
                  <Link href="/auth/sign-up" className="theme-btn-twelve d-none d-lg-block">
                    Get Started
                  </Link>
                </li>
              </>
            )}
          </ul>
        </div>
        <nav id="mega-menu-holder" className="navbar navbar-expand-lg pl-xl-5 ml-xl-5 order-lg-2">
          <div className="nav-container">
            <button
              className="custom-mobile-toggle d-lg-none"
              onClick={toggleMobileMenu}
              aria-label="Toggle mobile menu"
            >
              <span className={`hamburger ${isMobileMenuOpen ? "open" : ""}`}>
                <span></span>
                <span></span>
                <span></span>
              </span>
            </button>
            <div className="d-none d-lg-flex justify-content-between align-items-center">
              <ul className="navbar-nav main-side-nav font-gordita" id="one-page-nav">
                {navItems.map((item) => (
                  <li
                    key={item.key}
                    className={`nav-item ${item.dropdown ? `dropdown ${activeMenuItem === item.key ? "active" : ""}` : ""}`}
                  >
                    <Link
                      href={item.path}
                      className={`nav-link ${item.dropdown ? "dropdown-toggle" : ""} ${activeMenuItem === item.key ? "active" : ""}`}
                      onClick={(e) => {
                        if (item.dropdown && activeMenuItem === item.key) {
                          e.preventDefault();
                        }
                      }}
                    >
                      {item.name}
                    </Link>
                    {item.dropdown && (
                      <ul className="dropdown-menu font-rubik">
                        <li>
                          <Link
                            href="/support"
                            className={`dropdown-item ${pathname === "/support" ? "active" : ""}`}
                          >
                            FAQs
                          </Link>
                        </li>
                        <li>
                          <Link
                            href="/support/legal"
                            className={`dropdown-item ${isTermsPoliciesActive ? "active" : ""}`}
                          >
                            Terms & policies
                          </Link>
                        </li>
                      </ul>
                    )}
                  </li>
                ))}
              </ul>
            </div>
            {isMobileMenuOpen && (
              <div className="custom-mobile-menu d-lg-none">
                <ul className="custom-mobile-nav font-gordita">
                  {navItems.map((item) => (
                    <li
                      key={item.key}
                      className={`nav-item ${item.dropdown ? "dropdown" : ""}`}
                    >
                      {item.dropdown ? (
                        <>
                          <div
                            className="nav-link support-toggle"
                            onClick={() => setIsSupportOpen(!isSupportOpen)}
                          >
                            {item.name}
                            <span className={`chevron ${isSupportOpen ? "up" : "down"}`}></span>
                          </div>
                          <ul className={`custom-mobile-dropdown font-rubik ${isSupportOpen ? "show" : ""}`}>
                            <li>
                              <Link
                                href="/support"
                                className={`dropdown-item ${pathname === "/support" ? "active" : ""}`}
                                onClick={() => setIsMobileMenuOpen(false)}
                              >
                                FAQs
                              </Link>
                            </li>
                            <li>
                              <Link
                                href="/support/legal"
                                className={`dropdown-item ${isTermsPoliciesActive ? "active" : ""}`}
                                onClick={() => setIsMobileMenuOpen(false)}
                              >
                                Terms & policies
                              </Link>
                            </li>
                            <li>
                              <Link
                                href="/"
                                className={`dropdown-item ${activeMenuItem === "home" ? "active" : ""}`}
                                onClick={() => setIsMobileMenuOpen(false)}
                              >
                                Contacts
                              </Link>
                            </li>
                          </ul>
                        </>
                      ) : (
                        <Link
                          href={item.path}
                          className={`nav-link ${activeMenuItem === item.key ? "active" : ""}`}
                          onClick={() => setIsMobileMenuOpen(false)}
                        >
                          {item.name}
                        </Link>
                      )}
                    </li>
                  ))}
                </ul>
                {!session && (
                  <div className="custom-mobile-footer">
                    <Link
                      href="/auth"
                      className="mobile-signin d-flex align-items-center"
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      <Image src="/images/icon/52.svg" alt="Login Icon" width={24} height={24} />
                      <span>Login</span>
                    </Link>
                    <Link
                      href="/auth/sign-up"
                      className="mobile-signup"
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      Sign Up
                    </Link>
                  </div>
                )}
              </div>
            )}
          </div>
        </nav>
        {isUserMenuOpen && session && session.user && (
          <div className="custom-user-menu d-lg-none">
            <div className="custom-mobile-header">
              <div className="user-header">
                <Image
                  src={avatarUrl}
                  alt="User Avatar"
                  className="user-avatar"
                  width={50}
                  height={50}
                  onError={(e) => {
                    (e.target as HTMLImageElement).src = "/images/users/avatar/fallback.webp";
                  }}
                />
                <div className="user-header-info">
                  <span id="name">Hey, {userName}</span>
                  <span id="email">{session.user.email || "No email"}</span>
                </div>
              </div>
              <div
                id="custom-user-menu-close"
                onClick={() => setIsUserMenuOpen(false)}
              ></div>
            </div>
            <ul className="custom-mobile-nav font-gordita">
              <li className="nav-item">
                <Link
                  href="/dashboard/orders"
                  className={`nav-link ${pathname === "/dashboard/orders" ? "active" : ""}`}
                  onClick={() => setIsUserMenuOpen(false)}
                >
                  My Orders
                </Link>
              </li>
              <li className="nav-item">
                <Link
                  href="/dashboard/projects"
                  className={`nav-link ${pathname === "/dashboard/projects" ? "active" : ""}`}
                  onClick={() => setIsUserMenuOpen(false)}
                >
                  My Projects
                </Link>
              </li>
              <li className="nav-item">
                <Link
                  href="/dashboard/account"
                  className={`nav-link ${pathname === "/dashboard/account" ? "active" : ""}`}
                  onClick={() => setIsUserMenuOpen(false)}
                >
                  My Profile
                </Link>
              </li>
            </ul>
            <div className="custom-mobile-footer">
              <button
                className="mobile-signout d-flex align-items-center"
                onClick={async () => {
                  try {
                    const token = session?.user?.token;
                    if (!token) {
                      console.warn("No authentication token available for token revocation");
                      return;
                    }
                    const apiUrl = process.env.NEXT_PUBLIC_API_URL;
                    if (!apiUrl) {
                      console.error("NEXT_PUBLIC_API_URL is not defined in environment variables");
                      return;
                    }
                    const response = await fetch(`${apiUrl}/auth/revoke`, {
                      method: "POST",
                      headers: {
                        "Content-Type": "application/json",
                        "Authorization": `Bearer ${token}`,
                      },
                      body: JSON.stringify({
                        user_id: session?.user?.id,
                        userAgent: navigator.userAgent || "Unknown",
                        ipAddress: "Unknown",
                      }),
                    });
                    if (!response.ok) {
                      console.error("Failed to revoke token:", await response.text());
                    }
                  } catch (error) {
                    console.error("Error revoking token:", error);
                  }
                  await signOut({ redirect: true, callbackUrl: "/auth" });
                }}
              >
                <Image src="/images/icon/52.svg" alt="Sign Out Icon" width={24} height={24} />
                <span>Sign Out</span>
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}